import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import api from '../utils/api';
import Icon from '../components/Icons';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { Users, FolderKanban, DollarSign, TrendingUp, CheckCircle, Clock, CalendarDays, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

const COLORS = ['#7B0D0D', '#a01212', '#c73333', '#e05555', '#f07777'];

function StatCard({ icon: Icon, value, label, color = 'red', prefix = '', suffix = '' }) {
  const colorMap = { red: 'stat-icon-red', green: 'stat-icon-green', blue: 'stat-icon-blue', yellow: 'stat-icon-yellow', gray: 'stat-icon-gray' };
  return (
    <div className="stat-card">
      <div className={`stat-icon ${colorMap[color]}`}><Icon size={22} /></div>
      <div>
        <div className="stat-value">{prefix}{value}{suffix}</div>
        <div className="stat-label">{label}</div>
      </div>
    </div>
  );
}

function getBadgeEstado(estado) {
  const map = { programada: 'badge-blue', completada: 'badge-green', cancelada: 'badge-red' };
  return <span className={`badge ${map[estado] || 'badge-gray'}`}>{estado}</span>;
}

export default function DashboardPage() {
  const { user, isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [adminData, setAdminData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [meRes] = await Promise.all([api.get('/dashboard/me')]);
        setData(meRes.data);
        if (isAdmin) {
          const adminRes = await api.get('/dashboard/admin');
          setAdminData(adminRes.data);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isAdmin]);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
      <div className="spinner spinner-dark" style={{ width: 36, height: 36 }} />
    </div>
  );

  const estadosData = data ? [
    { name: 'Activos', value: data.resumen.proyectosActivos || 0 },
    { name: 'Finalizados', value: data.resumen.proyectosFinalizados || 0 },
    { name: 'En pausa', value: data.resumen.proyectosPausa || 0 },
    { name: 'Pendientes', value: data.resumen.proyectosPendientes || 0 },
  ].filter(d => d.value > 0) : [];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Bienvenido de vuelta, {user?.nombre} </p>
        </div>
        <Link to="/citas" className="btn btn-primary">
          <CalendarDays size={16} /> Nueva cita
        </Link>
      </div>

      {/* KPIs personales */}
      <div className="grid-4" style={{ marginBottom: 24 }}>
        <StatCard icon={Users} value={data?.resumen.totalClientes || 0} label="Mis clientes" color="blue" />
        <StatCard icon={FolderKanban} value={data?.resumen.totalProyectos || 0} label="Mis proyectos" color="red" />
        <StatCard icon={CheckCircle} value={data?.kpis.tasaCierre || 0} label="Tasa de cierre" suffix="%" color="green" />
        {isAdmin && (
          <StatCard icon={TrendingUp} value={data?.kpis.margenPromedio || 0} label="Margen promedio" suffix="%" color="yellow" />
        )}
      </div>

      <div className="grid-2" style={{ marginBottom: 24 }}>
        {/* Financiero */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Resumen financiero (sin IVA)</h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {[
              { label: 'Presupuesto total (excl. dados de baja)', value: data?.financiero.presupuestoTotal, color: '#7B0D0D' },
              { label: 'Presupuesto aprobado', value: data?.financiero.presupuestoAprobado, color: '#2563eb' },
              { label: 'Presupuesto pendientes', value: data?.financiero.presupuestoPendientes, color: '#d97706' },
              { label: 'Costo en pendientes', value: data?.financiero.costoPendientes, color: '#92400e' },
              { label: 'Utilidad en pendientes', value: data?.financiero.utilidadPendientes, color: '#16a34a' },
            ].map(item => (
              <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: '#fafafa', borderRadius: 8 }}>
                <span style={{ fontSize: 14, color: '#555' }}>{item.label}</span>
                <span style={{ fontSize: 16, fontWeight: 700, color: item.color }}>${parseFloat(item.value || 0).toLocaleString('es-EC', { minimumFractionDigits: 2 })}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Proyectos por estado */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Estado de proyectos</h3>
          </div>
          {estadosData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={estadosData} cx="50%" cy="50%" outerRadius={70} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                  {estadosData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="empty-state" style={{ padding: 30 }}>
              <p>Aún no tienes proyectos registrados</p>
            </div>
          )}
        </div>
      </div>

      {/* Próximas citas */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <h3 className="card-title">Próximas citas</h3>
          <Link to="/citas" className="btn btn-secondary btn-sm">Ver todas</Link>
        </div>
        {data?.citasProximas?.length > 0 ? (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Empresa / Lugar</th>
                  <th>Fecha</th>
                  <th>Hora</th>
                  <th>Tema</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {data.citasProximas.map(c => (
                  <tr key={c.id}>
                    <td><strong>{c.empresaLugar}</strong>{c.client && <div style={{ fontSize: 12, color: '#888' }}>{c.client.empresa}</div>}</td>
                    <td>{c.fecha}</td>
                    <td>{c.hora?.slice(0, 5)}</td>
                    <td style={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.tema}</td>
                    <td>{getBadgeEstado(c.estado)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state" style={{ padding: 24 }}>
            <CalendarDays size={36} />
            <h3>Sin citas próximas</h3>
            <p>Agenda una cita para comenzar</p>
          </div>
        )}
      </div>

      {/* Panel admin */}
      {isAdmin && adminData && (
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, color: '#7B0D0D' }}>Vista Administrador — Métricas generales (sin IVA)</h2>
          <div className="grid-4" style={{ marginBottom: 24 }}>
            <StatCard icon={Users} value={adminData.resumen.totalUsuarios} label="Usuarios activos" color="blue" />
            <StatCard icon={Users} value={adminData.resumen.totalClientes} label="Total clientes" color="red" />
            <StatCard icon={FolderKanban} value={adminData.resumen.totalProyectos} label="Total proyectos" color="gray" />
            <StatCard icon={DollarSign} value={`$${parseFloat(adminData.financiero.presupuestoTotal).toLocaleString('es-EC')}`} label="Presupuesto total (sin IVA)" color="green" />
          </div>
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Rendimiento por asesor</h3>
              <Link to="/admin" className="btn btn-secondary btn-sm">Ver detalles</Link>
            </div>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Asesor</th>
                    <th>Proyectos</th>
                    <th>Activos</th>
                    <th>Finalizados</th>
                    <th>Presupuesto (sin IVA)</th>
                    <th>Utilidad (sin IVA)</th>
                  </tr>
                </thead>
                <tbody>
                  {adminData.metricasPorUsuario.map(m => (
                    <tr key={m.usuario?.id}>
                      <td><strong>{m.usuario?.nombre} {m.usuario?.apellido}</strong></td>
                      <td>{m.proyectos}</td>
                      <td><span className="badge badge-blue">{m.activos}</span></td>
                      <td><span className="badge badge-green">{m.finalizados}</span></td>
                      <td style={{ fontWeight: 600 }}>${parseFloat(m.presupuestoTotal).toLocaleString('es-EC', { minimumFractionDigits: 2 })}</td>
                      <td style={{ fontWeight: 600, color: '#16a34a' }}>${parseFloat(m.utilidadTotal).toLocaleString('es-EC', { minimumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
